import {
  ApiFehler,
  type BelegAntwort,
  type BelegAuftrag,
  type KassenApi,
  type Vorgang,
  type VorgangsStand,
} from './api';
import type { GeraeteSpeicher } from './geraetespeicher';
import type { OfflineBeleg } from './offline';

/**
 * N1 (Paketplan v4.1, Punkt 1): Vorgangsdatensatz der Kasse. Er wird **vor dem ersten
 * Sendeversuch** dauerhaft im Gerätespeicher (IndexedDB `bilanx-kassa`, Bereich `vorgang`)
 * angelegt: Vorgangs-ID, Belegnummer, ursprünglicher Zeitpunkt und der vollständige Auftrag.
 * Online-Aufruf und eine spätere Offline-Nachsendung tragen so dieselbe Vorgangs-ID und denselben
 * Inhalt; der Server erkennt eine Wiederholung nur daran. Scheitert das Anlegen, wird nicht
 * gesendet.
 */
export interface VorgangsDatensatz {
  kassenId: string;
  belegnummer: string;
  vorgang: Vorgang;
  /** Gesendeter Auftrag (mit `vorgang`). */
  auftrag: BelegAuftrag;
  /** Darf der Verkauf bei Verbindungsausfall offline gepuffert werden (Nummernvergabe)? */
  offlineErlaubt: boolean;
  /**
   * Fehlerbericht 25.10.2026: Beim Anlegen war das Gerät nachweislich ohne Verbindung
   * (`navigator.onLine === false`); der Auftrag wurde deshalb gar nicht gesendet. Ohne dieses
   * Feld kann der erste Sendeversuch den Server erreicht haben (verlorene Antwort).
   */
  ohneVerbindung?: true;
  /**
   * Codex-Runde 1 (Fehlerbericht 25.10.2026, K1/K6/M7): Bezahlung durch einen Menschen an der
   * Kasse bestätigt – dauerhaft vor dem (erneuten) Senden festgehalten, mit Person und Zeit. Erst
   * damit darf der Vorgang auch im Hintergrund nachgesendet werden; ohne ihn bucht nur die
   * Bezahlentscheidung.
   */
  bezahlt?: Bezahlbestaetigung;
  /**
   * Codex-Runde 1 (K6): Ersatzvorgang für einen bezahlten Verkauf, dessen Nummer am Server schon
   * einem anderen Beleg gehört – Vorgangs-ID des ersetzten Vorgangs. Je ersetztem Vorgang gibt es
   * höchstens einen Ersatz.
   */
  ersatzFuer?: string;
  /**
   * Codex-Prüfung 10: Browsersitzung, die den Vorgang angelegt hat (übersteht Neuladen und
   * Duplizieren eines Fensters) – ihr Bon ist der Einkauf dieses Vorgangs, auch wenn der Abbruch
   * zwischen Vorgangssicherung und Bon-Merker lag.
   */
  sitzung?: string;
  /** Codex-Bestätigungsprüfung F6: Stelle in der Ersatzkette (1 = erster Ersatz), dauerhaft. */
  ersatzTiefe?: number;
  /**
   * Codex N4-1: Abschlussnachweis – der Datensatz bleibt nach dem Abschluss erhalten (statt
   * gelöscht zu werden), damit jede spätere Klärung desselben Bons (auch über einen alten Merker
   * ohne Vorgangs-ID) das Ergebnis findet und nie über die Belegnummer rät. Aufgeräumt nach
   * NACHWEIS_AUFBEWAHREN_MS.
   */
  abgeschlossen?: VorgangsAbschluss;
}

export type VorgangsAbschluss =
  | { art: 'gebucht'; beleg: BelegAntwort; am: string }
  | { art: 'nicht_gebucht'; grund: string; am: string; ersetztDurch?: string }
  | { art: 'gepuffert'; am: string };

/** Wer hat wann und wie bestätigt, dass der Kunde bezahlt hat (Codex-Runde 1, M7). */
export interface Bezahlbestaetigung {
  am: string;
  /** Bediener bzw. ausdrücklich genannte Person – nie leer */
  von: string;
  /** entscheidung = „Beleg prüfen“; offline = vor dem Puffern; ersatz = übernommen vom Original */
  quelle: 'entscheidung' | 'offline' | 'ersatz';
}

/** Abschlussnachweise werden nach 7 Tagen aufgeräumt. */
export const NACHWEIS_AUFBEWAHREN_MS = 7 * 24 * 3_600_000;

function schluessel(kassenId: string, belegnummer: string): string {
  return `${kassenId}|${belegnummer}`;
}

/** Datensatz anlegen – nie überschreiben (eine Nummer gehört genau zu einem Vorgang). */
export async function sichereVorgang(
  speicher: GeraeteSpeicher,
  d: VorgangsDatensatz,
): Promise<void> {
  await speicher.transaktion((tx) => tx.add('vorgang', schluessel(d.kassenId, d.belegnummer), d));
}

export async function ladeVorgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
): Promise<VorgangsDatensatz | null> {
  return speicher.transaktion(
    async (tx) =>
      (await tx.get<VorgangsDatensatz>('vorgang', schluessel(kassenId, belegnummer))) ?? null,
  );
}

/**
 * Ausgang festhalten, sobald er feststeht (Beleg erstellt bzw. nachweislich nicht gebucht): Der
 * Datensatz wird zum Abschlussnachweis (Codex N4-1), nicht gelöscht.
 */
export async function schliesseVorgangAb(
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
  /** Stop-Prüfung: nur genau dieser Vorgang – nie ein fremder Datensatz gleicher Nummer */
  vorgangId: string,
  abschluss:
    | { art: 'gebucht'; beleg: BelegAntwort }
    | { art: 'nicht_gebucht'; grund: string; ersetztDurch?: string }
    | { art: 'gepuffert' },
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    const d = await tx.get<VorgangsDatensatz>('vorgang', schluessel(kassenId, belegnummer));
    // Ein vorläufiger Nachweis (nicht gebucht, gepuffert) darf vom Server-Ergebnis „gebucht“
    // abgelöst werden (Codex-Runde 5); „gebucht“ selbst ist endgültig
    if (d?.vorgang.id !== vorgangId) {
      return;
    }
    const abloesbar =
      d.abgeschlossen === undefined ||
      (abschluss.art === 'gebucht' && d.abgeschlossen.art !== 'gebucht');
    if (abloesbar) {
      await tx.put('vorgang', schluessel(kassenId, belegnummer), {
        ...d,
        abgeschlossen: { ...abschluss, am: new Date().toISOString() },
      } satisfies VorgangsDatensatz);
    }
  });
}

/**
 * Codex-Runde 1 (K1/M7): Bezahlbestätigung dauerhaft am offenen Vorgang festhalten – vor jedem
 * Senden bzw. Puffern auf Grund dieser Entscheidung. Nur genau dieser, noch offene Vorgang; eine
 * schon vorhandene Bestätigung bleibt (erste Entscheidung zählt). Ergebnis: der Datensatz.
 */
export async function halteBezahlungFest(
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
  vorgangId: string,
  bestaetigung: Bezahlbestaetigung,
): Promise<VorgangsDatensatz> {
  if (bestaetigung.von.trim() === '') {
    throw new Error('Bezahlbestätigung ohne Person');
  }
  return speicher.transaktion(async (tx) => {
    const d = await tx.get<VorgangsDatensatz>('vorgang', schluessel(kassenId, belegnummer));
    if (d?.vorgang.id !== vorgangId || d.abgeschlossen !== undefined) {
      throw new Error(`Vorgang ${belegnummer} ist nicht mehr offen`);
    }
    if (d.bezahlt !== undefined) {
      return d;
    }
    // Codex-Prüfung 14: nach der Bestätigung wird gesendet – „ohne Verbindung angelegt“ ist ab
    // jetzt kein Nachweis mehr, dass nie gesendet wurde
    const neu: VorgangsDatensatz = { ...d, bezahlt: bestaetigung };
    delete neu.ohneVerbindung;
    await tx.put('vorgang', schluessel(kassenId, belegnummer), neu);
    return neu;
  });
}

/** Abschlussnachweise nach der Aufbewahrungsfrist entfernen (nur abgeschlossene). */
export async function raeumeNachweiseAuf(
  speicher: GeraeteSpeicher,
  jetzt: () => number = Date.now,
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    for (const d of await tx.alle<VorgangsDatensatz>('vorgang')) {
      if (
        d.abgeschlossen !== undefined &&
        jetzt() - Date.parse(d.abgeschlossen.am) > NACHWEIS_AUFBEWAHREN_MS
      ) {
        await tx.delete('vorgang', schluessel(d.kassenId, d.belegnummer));
      }
    }
  });
}

/**
 * Puffer-Eintrag aus dem Datensatz: genau die gesendeten Nutzdaten (gleicher Hash am Server) mit
 * dem ursprünglichen Zeitpunkt. null = offline nicht pufferbar (kein Barverkauf, Gutscheine,
 * Zahlungsreferenz, Trinkgeld oder Sonderbeleg).
 */
export function offlineBelegAusVorgang(
  d: VorgangsDatensatz,
  bedienerSitzung?: string,
): OfflineBeleg | null {
  const a = d.auftrag;
  if (
    a.belegtyp !== 'STANDARD' ||
    a.zahlungsart !== 'bar' ||
    a.positionen === undefined ||
    a.zahlungsreferenz !== undefined ||
    (a.gutscheine?.length ?? 0) > 0 ||
    (a.gutscheinAusgaben?.length ?? 0) > 0 ||
    a.sonderbeleg !== undefined ||
    a.betraege !== undefined ||
    a.stornoVon !== undefined
  ) {
    return null;
  }
  return {
    kassenId: d.kassenId,
    belegnummer: d.belegnummer,
    zeitpunkt: d.vorgang.zeitpunkt,
    positionen: a.positionen,
    zahlungsart: 'bar',
    ...(a.empfaenger === undefined ? {} : { empfaenger: a.empfaenger }),
    ...(a.notiz === undefined ? {} : { notiz: a.notiz }),
    ...(a.bediener === undefined ? {} : { bediener: a.bediener }),
    ...(a.kundeId === undefined ? {} : { kundeId: a.kundeId }),
    ...(a.gegebenCent === undefined ? {} : { gegebenCent: a.gegebenCent }),
    ...(a.trinkgeldCent === undefined ? {} : { trinkgeldCent: a.trinkgeldCent }),
    ...(bedienerSitzung === undefined ? {} : { bedienerSitzung }),
    vorgang: d.vorgang,
  };
}

/** Ergebnis von „Beleg prüfen“ für einen offenen Vorgang. */
export type VorgangsKlaerung =
  | {
      art: 'gebucht';
      beleg: BelegAntwort;
      /** Codex-Runde 1 (K6): unter neuer Nummer als Ersatz für diese Nummer gebucht */
      ersatzFuer?: string;
    }
  | { art: 'nicht_gebucht'; fehler: string }
  | { art: 'unklar'; fehler: string }
  /** offline gepuffert – der Verkauf ist gebucht (Nachsignierung folgt) */
  | { art: 'gepuffert' }
  | { art: 'ohne_datensatz' }
  /**
   * Fehlerbericht 25.10.2026 (Zustand B/C): Der Server kennt den Verkauf nicht (weder gebucht
   * noch verworfen noch in Klärung). Ob gebucht wird, entscheidet ein Mensch an der Kasse:
   * Bezahlung angenommen → derselbe Vorgang wird gebucht; nicht angenommen → verworfen.
   * Bis dahin bleibt das Kassieren gesperrt (Zustand D).
   */
  | {
      art: 'entscheidung';
      belegnummer: string;
      summeCent: number;
      /** Auftrag wurde nachweislich nie gesendet (Gerät war ohne Verbindung). */
      ohneVerbindung: boolean;
      /**
       * Codex-Prüfung 13: Bezahlung schon bestätigt, Buchung aber fachlich abgelehnt – Grund der
       * Ablehnung („Ja“ versucht erneut, „Nein“ hält fest, dass der Betrag zurückgegeben wurde).
       */
      fehler?: string;
    };

/**
 * Entscheidung an der Kasse, ob der ungeklärte Verkauf bezahlt wurde (Zustand B/C), immer mit
 * der entscheidenden Person (Codex-Runde 1, M7).
 */
export type Bezahlentscheidung =
  { bezahlt: true; von: string } | { bezahlt: false; grund: string; von: string };

/**
 * Braucht der Verkauf eine ausdrückliche Bezahlentscheidung, bevor er gebucht wird? Ja für jeden
 * Standardverkauf ohne bestätigte Kartenzahlung (bar, Gutschein, Teilbeträge): Nur das Personal
 * weiß, ob das Geld angenommen wurde. Mit Zahlungsreferenz (Karte) ist die Zahlung am Terminal
 * bestätigt (Regel 3) – der Beleg wird mit demselben Vorgang automatisch nachgeholt.
 */
export function brauchtBezahlentscheidung(d: VorgangsDatensatz): boolean {
  return (
    d.auftrag.belegtyp === 'STANDARD' &&
    d.auftrag.zahlungsreferenz === undefined &&
    d.bezahlt === undefined
  );
}

function summeDes(d: VorgangsDatensatz): number {
  return (d.auftrag.positionen ?? []).reduce((s, p) => s + p.bruttoCent, 0);
}

type VorgangsApi = Pick<KassenApi, 'erstelleBeleg' | 'vorgangVerwerfen' | 'vorgangStand'> &
  Partial<Pick<KassenApi, 'beleg'>>;

/** Endgültiger Stand vom Server → Ergebnis; „klaerung“/„unbekannt“ bleiben unklar. */
async function ausStand(
  stand: VorgangsStand,
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
  vorgangId: string,
): Promise<VorgangsKlaerung> {
  if (stand.stand === 'gebucht') {
    await schliesseVorgangAb(speicher, kassenId, belegnummer, vorgangId, {
      art: 'gebucht',
      beleg: stand.beleg,
    }).catch(() => undefined);
    return { art: 'gebucht', beleg: stand.beleg };
  }
  if (stand.stand === 'verworfen') {
    await schliesseVorgangAb(speicher, kassenId, belegnummer, vorgangId, {
      art: 'nicht_gebucht',
      grund: stand.grund,
    }).catch(() => undefined);
    return { art: 'nicht_gebucht', fehler: stand.grund };
  }
  return {
    art: 'unklar',
    fehler:
      stand.stand === 'klaerung'
        ? 'Verkauf ist in Klärung im Backoffice'
        : 'Ausgang am Server noch unbekannt',
  };
}

/**
 * N1 (Codex N1-2, R2-1, R3-1): offenen Verkauf klären. Mit gesichertem Vorgangsdatensatz wird
 * genau dieser Auftrag mit derselben Vorgangs-ID wiederholt: Der Server liefert den vorhandenen
 * Beleg (auch wenn der erste Aufruf noch unterwegs ist – beide laufen unter der Kassensperre)
 * oder erstellt ihn jetzt; ein zweiter Umsatz entsteht nie.
 *
 * „Nicht gebucht“ gilt nur, wenn der Server den Vorgang unter der Kassensperre endgültig als
 * verworfen festhält (danach bucht auch ein verspäteter Erstaufruf nie mehr) – keine Zeitregel.
 * Verworfen wird nur nach einer fachlichen Ablehnung NACH der Vorgangsprüfung
 * (`vorgangGeprueft`): bei vergebener Nummer immer (der Vorgang kann nie gelingen), sonst nur
 * mit `verwerfen` (bewusster Klärungsschritt an der Kasse, nicht im Hintergrund).
 * Alles andere (Anmeldung, Sperre, Versionszaun, Netz, Server, Konflikt, Lesefehler) bleibt
 * unklar: Datensatz und Sperrmerker bleiben.
 */
export async function klaereOffenenVorgang(
  api: VorgangsApi,
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
  optionen: Parameters<typeof klaereOffenenVorgangRoh>[4] = { verwerfen: true },
): Promise<VorgangsKlaerung> {
  const k = await klaereOffenenVorgangRoh(api, speicher, kassenId, belegnummer, optionen);
  if (k.art !== 'nicht_gebucht') {
    return k;
  }
  // Codex-Bestätigungsprüfung F1: Gibt es zu diesem Vorgang einen Ersatzvorgang (dauerhaft vor
  // dem Verwerfen angelegt), zählt dessen Ausgang – auch wenn die Ersatzverknüpfung am Original
  // fehlt (z. B. Antwort beim Verwerfen verloren). Nie „nicht gebucht“ für einen ersetzten Verkauf.
  let d: VorgangsDatensatz | null;
  try {
    d = await ladeVorgang(speicher, kassenId, belegnummer);
  } catch (e: unknown) {
    // Codex-Prüfung 3 (Fund 2): Lesefehler – ob ein Ersatz besteht, ist offen; nie „nicht gebucht“
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  return d === null
    ? k
    : folgeErsatz(api, speicher, d.kassenId, d.vorgang.id, belegnummer, k, optionen);
}

/** Ersatzvorgang zu einem (verworfenen) Vorgang suchen und dessen Ausgang liefern. */
async function folgeErsatz(
  api: VorgangsApi,
  speicher: GeraeteSpeicher,
  kassenId: string,
  vorgangId: string,
  belegnummer: string,
  k: VorgangsKlaerung,
  optionen: {
    verwerfen: boolean;
    neueNummer?: () => Promise<{ nummer: string; offlineErlaubt: boolean }>;
    nummernkreis?: Nummernkreis;
    entscheidung?: Bezahlentscheidung;
  },
): Promise<VorgangsKlaerung> {
  let ersatz: VorgangsDatensatz | undefined;
  try {
    ersatz = (await speicher.transaktion((tx) => tx.alle<VorgangsDatensatz>('vorgang'))).find(
      (x) => x.kassenId === kassenId && x.ersatzFuer === vorgangId,
    );
  } catch (e: unknown) {
    // Lesefehler: ob ein Ersatz besteht, ist offen – nie „nicht gebucht“ melden
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  if (ersatz === undefined) {
    // Codex-Prüfung 12/13: Ersatzdatensatz schon aufgeräumt (7 Tage) – der Verwerfungsgrund am
    // Server nennt ihn mit Vorgangs-ID („ersetzt durch … (Vorgang …)“). Die Kette wird über die
    // Vorgangs-IDs am Server verfolgt (nie über eine Nummer, die einem anderen Beleg gehören
    // kann); ohne Nachweis unklar, nie „nicht gebucht“
    let grund = k.art === 'nicht_gebucht' ? k.fehler : '';
    if (!grund.includes('ersetzt durch')) {
      return k;
    }
    for (let schritt = 0; schritt <= ERSATZ_HOECHSTENS * 2; schritt++) {
      const id = /ersetzt durch \S+ \(Vorgang ([0-9A-Za-z-]{8,100})\)/.exec(grund)?.[1];
      if (id === undefined) {
        return {
          art: 'unklar',
          fehler: 'Verkauf wurde ersetzt – Ersatz nicht nachweisbar, Belegjournal prüfen',
        };
      }
      let stand: VorgangsStand;
      try {
        stand = await api.vorgangStand(kassenId, id);
      } catch (f: unknown) {
        return { art: 'unklar', fehler: f instanceof Error ? f.message : String(f) };
      }
      if (stand.stand === 'gebucht') {
        return { art: 'gebucht', beleg: stand.beleg, ersatzFuer: belegnummer };
      }
      if (stand.stand !== 'verworfen') {
        return { art: 'unklar', fehler: 'Ersatzvorgang am Server offen bzw. in Klärung' };
      }
      grund = stand.grund;
      if (!grund.includes('ersetzt durch')) {
        // Ersatz selbst ohne Folge verworfen: unklar (bezahlt bestätigt, nicht gebucht)
        return {
          art: 'unklar',
          fehler: `Ersatzvorgang verworfen (${grund}) – Belegjournal prüfen`,
        };
      }
    }
    return { art: 'unklar', fehler: 'Ersatzkette zu lang – Belegjournal prüfen' };
  }
  const e = await klaereOffenenVorgang(api, speicher, kassenId, ersatz.belegnummer, {
    verwerfen: optionen.verwerfen && optionen.entscheidung !== undefined,
    vorgangId: ersatz.vorgang.id,
    ...(optionen.entscheidung === undefined ? {} : { entscheidung: optionen.entscheidung }),
    ...(optionen.neueNummer === undefined ? {} : { neueNummer: optionen.neueNummer }),
    ...(optionen.nummernkreis === undefined ? {} : { nummernkreis: optionen.nummernkreis }),
  });
  return e.art === 'gebucht'
    ? { ...e, ersatzFuer: belegnummer }
    : e.art === 'nicht_gebucht'
      ? e
      : {
          art: 'unklar',
          fehler: e.art === 'unklar' ? e.fehler : `Ersatzvorgang ${ersatz.belegnummer} offen`,
        };
}

async function klaereOffenenVorgangRoh(
  api: VorgangsApi,
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
  optionen: {
    verwerfen: boolean;
    vorgangId?: string;
    /** Fehlerbericht 25.10.2026: Bezahlentscheidung an der Kasse (Zustand B/C). */
    entscheidung?: Bezahlentscheidung;
    /**
     * Codex-Runde 1 (K6): neue Nummer für einen Ersatzvorgang, falls die Nummer eines bezahlten
     * Verkaufs am Server schon einem anderen Beleg gehört. Ohne sie bleibt ein solcher Verkauf
     * offen (unklar) – er wird nie still als „nicht gebucht“ abgeschlossen.
     */
    neueNummer?: () => Promise<{ nummer: string; offlineErlaubt: boolean }>;
    /** intern: Zahl der Ersatzvorgänge in dieser Kette (höchstens ERSATZ_HOECHSTENS) */
    ersatzTiefe?: number;
    /**
     * Codex-Prüfung 3 (Fund 4): Ausweg nach ausgeschöpfter Ersatzkette – aktuelle Gerätekennung
     * und Markieren des Zählers als unsicher (die Synchronisation fordert eine neue Kennung an).
     * Mit neuer Kennung beginnt eine neue Nummernepoche: der nächste Ersatz kollidiert nicht.
     */
    nummernkreis?: Nummernkreis;
  } = { verwerfen: true },
): Promise<VorgangsKlaerung> {
  let d: VorgangsDatensatz | null;
  try {
    d = await ladeVorgang(speicher, kassenId, belegnummer);
  } catch (e: unknown) {
    // Lesefehler beweist nichts – nie auf die Nummernsuche ausweichen
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  // Codex-Runde 6: Ein Datensatz gleicher Nummer, aber anderer Vorgangs-ID gehört nicht zu
  // diesem Bon – nur genau derselbe Vorgang zählt
  if (d === null || (optionen.vorgangId !== undefined && d.vorgang.id !== optionen.vorgangId)) {
    return { art: 'ohne_datensatz' };
  }
  // Schon abgeschlossen (Abschlussnachweis, Codex N4-1). „gebucht“ ist endgültig; „nicht
  // gebucht“ und „gepuffert“ werden vor jeder Freigabe gegen den Server geprüft – eine spätere
  // Klärung oder Nacherfassung hat Vorrang (Codex-Runde 5). Ohne Verbindung: unklar.
  if (d.abgeschlossen !== undefined) {
    const a = d.abgeschlossen;
    if (a.art === 'gebucht') {
      return { art: 'gebucht', beleg: a.beleg };
    }
    if (a.art === 'nicht_gebucht' && a.ersetztDurch !== undefined) {
      // Codex-Runde 1 (K6): durch einen Ersatzvorgang abgelöst – dessen Ausgang zählt
      const k = await klaereOffenenVorgang(api, speicher, kassenId, a.ersetztDurch, {
        verwerfen: optionen.verwerfen,
        ersatzTiefe: (optionen.ersatzTiefe ?? 0) + 1,
        // Codex-Prüfung 14: die Entscheidung gilt dem Ersatz (z. B. „Betrag zurückgegeben“)
        ...(optionen.entscheidung === undefined ? {} : { entscheidung: optionen.entscheidung }),
        ...(optionen.neueNummer === undefined ? {} : { neueNummer: optionen.neueNummer }),
        ...(optionen.nummernkreis === undefined ? {} : { nummernkreis: optionen.nummernkreis }),
      });
      return k.art === 'gebucht' ? { ...k, ersatzFuer: belegnummer } : k;
    }
    let stand: VorgangsStand;
    try {
      stand = await api.vorgangStand(kassenId, d.vorgang.id);
    } catch (e: unknown) {
      return a.art === 'gepuffert'
        ? { art: 'gepuffert' }
        : { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
    }
    if (stand.stand === 'gebucht') {
      return ausStand(stand, speicher, kassenId, belegnummer, d.vorgang.id);
    }
    // Stop-Prüfung: offene Klärung am Server hat auch bei „gepuffert“ Vorrang – der Bon bleibt
    // unklar, bis im Backoffice entschieden ist
    if (stand.stand === 'klaerung') {
      return { art: 'unklar', fehler: 'Verkauf ist in Klärung im Backoffice' };
    }
    if (a.art === 'gepuffert') {
      return { art: 'gepuffert' };
    }
    // Nie gesendeter Verkauf (ohne Verbindung angelegt, nie bestätigt) wurde an der Kasse als
    // nicht bezahlt abgeschlossen: Er kann den Server nie erreicht haben
    if (stand.stand === 'unbekannt' && d.ohneVerbindung === true && d.bezahlt === undefined) {
      return { art: 'nicht_gebucht', fehler: a.grund };
    }
    return stand.stand === 'verworfen'
      ? { art: 'nicht_gebucht', fehler: a.grund }
      : { art: 'unklar', fehler: 'Ausgang am Server nicht bestätigt' };
  }
  // Fehlerbericht 25.10.2026: Ein Verkauf ohne bestätigte Kartenzahlung wird nie allein deshalb
  // gebucht, weil die Verbindung wieder da ist. Zuerst den Stand am Server abfragen:
  // A gebucht → vorhandenen Beleg übernehmen; verworfen → nicht gebucht; Klärung → unklar;
  // unbekannt → Mensch entscheidet (B: bezahlt → derselbe Vorgang; C: nicht bezahlt →
  // verwerfen); Server nicht erreichbar → unklar (D, Kassieren bleibt gesperrt).
  // Codex-Prüfung 13: „nicht bezahlt“ nach einer früheren Bestätigung (Betrag zurückgegeben,
  // Buchung fachlich nicht möglich) – wie C, nie senden
  const widerruf = optionen.entscheidung?.bezahlt === false && d.bezahlt !== undefined;
  if (brauchtBezahlentscheidung(d) || widerruf) {
    const entscheidung = optionen.entscheidung;
    let stand: VorgangsStand;
    try {
      stand = await api.vorgangStand(kassenId, d.vorgang.id);
    } catch (e: unknown) {
      // C ohne Verbindung: Ein nachweislich nie gesendeter Auftrag kann den Server nie erreicht
      // haben – an der Kasse als nicht bezahlt abschließen (Grund und Person im Gerätespeicher)
      if (entscheidung?.bezahlt === false && d.ohneVerbindung === true && d.bezahlt === undefined) {
        try {
          await schliesseVorgangAb(speicher, kassenId, belegnummer, d.vorgang.id, {
            art: 'nicht_gebucht',
            grund: entscheidung.grund,
          });
          return { art: 'nicht_gebucht', fehler: entscheidung.grund };
        } catch (f: unknown) {
          return { art: 'unklar', fehler: f instanceof Error ? f.message : String(f) };
        }
      }
      return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
    }
    if (stand.stand !== 'unbekannt') {
      return ausStand(stand, speicher, kassenId, belegnummer, d.vorgang.id);
    }
    if (entscheidung === undefined) {
      return {
        art: 'entscheidung',
        belegnummer,
        summeCent: summeDes(d),
        ohneVerbindung: d.ohneVerbindung === true,
      };
    }
    if (!entscheidung.bezahlt) {
      // C: keine Umsatzbuchung – am Server verbindlich als nicht gebucht festhalten (bucht auch
      // ein verspätet eintreffender Erstaufruf nie mehr); der Grund wird dort protokolliert
      try {
        const verworfen = await api.vorgangVerwerfen(
          kassenId,
          d.vorgang.id,
          belegnummer,
          entscheidung.grund.slice(0, 300),
        );
        return await ausStand(verworfen, speicher, kassenId, belegnummer, d.vorgang.id);
      } catch (e: unknown) {
        return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
      }
    }
    // B: bezahlt – zuerst dauerhaft festhalten (Person, Zeit), dann genau diesen Vorgang
    // senden (gleiche ID, gleiche Nummer, gleicher Inhalt). Geht die Antwort verloren, holt die
    // Wiederaufnahme ihn ohne erneute Frage nach (Codex-Runde 1, K1/M7).
    try {
      d = await halteBezahlungFest(speicher, kassenId, belegnummer, d.vorgang.id, {
        am: new Date().toISOString(),
        von: entscheidung.von,
        quelle: 'entscheidung',
      });
    } catch (e: unknown) {
      return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
    }
  }
  try {
    const beleg = await api.erstelleBeleg(kassenId, d.auftrag);
    await schliesseVorgangAb(speicher, kassenId, belegnummer, d.vorgang.id, {
      art: 'gebucht',
      beleg,
    }).catch(() => undefined);
    return { art: 'gebucht', beleg };
  } catch (e: unknown) {
    const text = e instanceof Error ? e.message : String(e);
    if (e instanceof ApiFehler && e.daten?.vorgangVerworfen === true) {
      await schliesseVorgangAb(speicher, kassenId, belegnummer, d.vorgang.id, {
        art: 'nicht_gebucht',
        grund: text,
      }).catch(() => undefined);
      return { art: 'nicht_gebucht', fehler: text };
    }
    const nachweis =
      e instanceof ApiFehler &&
      e.status >= 400 &&
      e.status < 500 &&
      e.daten?.vorgangGeprueft === true &&
      e.daten.klaerungsbeduerftig !== true &&
      e.daten.vorgangKonflikt !== true;
    if (!nachweis || (e.daten.belegnummerVergeben !== true && !optionen.verwerfen)) {
      return { art: 'unklar', fehler: text };
    }
    // Codex-Prüfung 13: Bestätigt bezahlter Verkauf, Buchung fachlich abgelehnt (z. B. Preis
    // geändert, Rabattrecht fehlt) – nie still „nicht gebucht“: bezahlt und ungeklärt lassen;
    // die Kasse fragt erneut (nach Behebung „Ja“, sonst „Nein“ = Betrag zurückgegeben)
    if (d.bezahlt !== undefined && e.daten.belegnummerVergeben !== true) {
      return {
        art: 'entscheidung',
        belegnummer,
        summeCent: summeDes(d),
        ohneVerbindung: false,
        fehler: text,
      };
    }
    // Codex-Runde 1 (K6): Bezahlter Verkauf, Nummer gehört schon einem anderen Beleg – nie als
    // „nicht gebucht“ zum erneuten Kassieren freigeben (der Kunde hat bezahlt), sondern
    // kontrolliert unter neuer Nummer als Ersatz buchen
    if (e.daten.belegnummerVergeben === true && d.bezahlt !== undefined) {
      return ersetzeVorgang(api, speicher, d, text, optionen);
    }
    try {
      const stand = await api.vorgangVerwerfen(
        kassenId,
        d.vorgang.id,
        belegnummer,
        text.slice(0, 300),
      );
      const k = await ausStand(stand, speicher, kassenId, belegnummer, d.vorgang.id);
      return k.art === 'nicht_gebucht' ? { art: 'nicht_gebucht', fehler: text } : k;
    } catch (f: unknown) {
      return { art: 'unklar', fehler: f instanceof Error ? f.message : String(f) };
    }
  }
}

/**
 * Codex-Runde 1 (K6): Ersatzvorgang für einen bezahlten Verkauf mit vergebener Nummer. Reihenfolge
 * (jeder Schritt wiederholbar, nie zwei Ersatzvorgänge):
 * 1. vorhandenen Ersatz (gleiche `ersatzFuer`) suchen, sonst neuen Vorgang mit neuer Nummer und
 *    übernommener Bezahlbestätigung anlegen;
 * 2. den alten Vorgang am Server verwerfen und als „ersetzt“ abschließen;
 * 3. den Ersatz senden. Scheitert das, bleibt er offen und bezahlt – die Wiederaufnahme holt ihn
 *    mit derselben Vorgangs-ID nach.
 */
async function ersetzeVorgang(
  api: VorgangsApi,
  speicher: GeraeteSpeicher,
  alt: VorgangsDatensatz,
  grund: string,
  optionen: {
    neueNummer?: () => Promise<{ nummer: string; offlineErlaubt: boolean }>;
    ersatzTiefe?: number;
    nummernkreis?: Nummernkreis;
  },
): Promise<VorgangsKlaerung> {
  const kassenId = alt.kassenId;
  // Codex-Bestätigungsprüfung F6: die Kettentiefe steht dauerhaft am Vorgang (nicht nur im Aufruf)
  let tiefe = Math.max(optionen.ersatzTiefe ?? 0, alt.ersatzTiefe ?? 0) + 1;
  let ersatz: VorgangsDatensatz | undefined;
  try {
    ersatz = (await speicher.transaktion((tx) => tx.alle<VorgangsDatensatz>('vorgang'))).find(
      (x) => x.kassenId === kassenId && x.ersatzFuer === alt.vorgang.id,
    );
    if (ersatz === undefined) {
      if (optionen.neueNummer === undefined || alt.bezahlt === undefined) {
        return {
          art: 'unklar',
          fehler: `Nummer ${alt.belegnummer} ist vergeben – Ersatzbuchung an der Kasse über „Beleg prüfen“`,
        };
      }
      // Nie endlos Nummern verbrauchen: nach ERSATZ_HOECHSTENS Ersatzvorgängen ist der
      // Nummernkreis dieser Gerätekennung gestört. Codex-Prüfung 3 (Fund 4): Ausweg über eine
      // neue Gerätekennung (neue Nummernepoche) – danach beginnt die Kette neu
      if (tiefe > ERSATZ_HOECHSTENS) {
        const kennungAlt = alt.belegnummer.split('-')[2];
        const kennungJetzt = await optionen.nummernkreis?.kennung().catch(() => undefined);
        if (kennungJetzt === undefined || kennungJetzt === kennungAlt) {
          await optionen.nummernkreis?.stoerung().catch(() => undefined);
          return {
            art: 'unklar',
            fehler: `Belegnummern dieser Kasse sind wiederholt schon vergeben – eine neue Gerätekennung wird angefordert (Verbindung nötig). Danach erneut „Beleg prüfen“; der Verkauf ${alt.belegnummer} ist bezahlt und wird dann gebucht. Nicht erneut kassieren.`,
          };
        }
        tiefe = 1;
      }
      const vergabe = await optionen.neueNummer();
      const vorgang = { id: crypto.randomUUID(), zeitpunkt: new Date().toISOString() };
      const vermerk = `Ersatz für ${alt.belegnummer} (Nummer vergeben, Bezahlung bestätigt)`;
      const notiz = alt.auftrag.notiz === undefined ? vermerk : `${alt.auftrag.notiz} – ${vermerk}`;
      ersatz = {
        kassenId,
        belegnummer: vergabe.nummer,
        vorgang,
        auftrag: {
          ...alt.auftrag,
          belegnummer: vergabe.nummer,
          notiz: notiz.slice(0, 500),
          vorgang,
        },
        offlineErlaubt: vergabe.offlineErlaubt,
        bezahlt: { ...alt.bezahlt, quelle: 'ersatz' },
        ersatzFuer: alt.vorgang.id,
        ersatzTiefe: tiefe,
        // Codex-Stop-Prüfung: der Ersatz gehört zum selben Bon (gleiche Browsersitzung)
        ...(alt.sitzung === undefined ? {} : { sitzung: alt.sitzung }),
      };
      await sichereVorgang(speicher, ersatz);
    }
  } catch (e: unknown) {
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  try {
    const stand = await api.vorgangVerwerfen(
      kassenId,
      alt.vorgang.id,
      alt.belegnummer,
      `ersetzt durch ${ersatz.belegnummer} (Vorgang ${ersatz.vorgang.id}) – ${grund}`.slice(0, 300),
    );
    if (stand.stand === 'gebucht') {
      // Doch gebucht (z. B. verspäteter Erstaufruf): kein Ersatz senden – der Ersatz wird als
      // nicht gebucht abgeschlossen (nie gesendet)
      await schliesseVorgangAb(speicher, kassenId, ersatz.belegnummer, ersatz.vorgang.id, {
        art: 'nicht_gebucht',
        grund: `Original ${alt.belegnummer} doch gebucht`,
      }).catch(() => undefined);
      return await ausStand(stand, speicher, kassenId, alt.belegnummer, alt.vorgang.id);
    }
    if (stand.stand !== 'verworfen') {
      return { art: 'unklar', fehler: 'Verkauf ist in Klärung im Backoffice' };
    }
    await schliesseVorgangAb(speicher, kassenId, alt.belegnummer, alt.vorgang.id, {
      art: 'nicht_gebucht',
      grund,
      ersetztDurch: ersatz.belegnummer,
    });
  } catch (e: unknown) {
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  const k = await klaereOffenenVorgang(api, speicher, kassenId, ersatz.belegnummer, {
    verwerfen: false,
    vorgangId: ersatz.vorgang.id,
    ersatzTiefe: tiefe,
    ...(optionen.neueNummer === undefined ? {} : { neueNummer: optionen.neueNummer }),
    ...(optionen.nummernkreis === undefined ? {} : { nummernkreis: optionen.nummernkreis }),
  });
  return k.art === 'gebucht' ? { ...k, ersatzFuer: alt.belegnummer } : k;
}

/** Gerätekennung lesen und gestörten Nummernkreis melden (Fund 4). */
export interface Nummernkreis {
  kennung: () => Promise<string>;
  stoerung: () => Promise<void>;
}

/** Höchstzahl automatischer Ersatzvorgänge je Verkauf und Gerätekennung (jeder verbraucht eine Nummer). */
export const ERSATZ_HOECHSTENS = 3;

/**
 * Codex R3-2: Bon-Merker eines N1-Vorgangs, dessen Datensatz fehlt (z. B. von der Wiederaufnahme
 * schon erledigt) – nie über die Belegnummer suchen, sondern den Stand der Vorgangs-ID abfragen.
 */
export async function klaereVorgangOhneDatensatz(
  api: VorgangsApi,
  speicher: GeraeteSpeicher,
  kassenId: string,
  belegnummer: string,
  vorgangId: string,
): Promise<VorgangsKlaerung> {
  let k: VorgangsKlaerung;
  try {
    k = await ausStand(
      await api.vorgangStand(kassenId, vorgangId),
      speicher,
      kassenId,
      belegnummer,
      vorgangId,
    );
  } catch (e: unknown) {
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  // Codex-Bestätigungsprüfung F1: ein ersetzter Vorgang ist nie „nicht gebucht“
  return k.art === 'nicht_gebucht'
    ? folgeErsatz(api, speicher, kassenId, vorgangId, belegnummer, k, { verwerfen: false })
    : k;
}

/** Alle offenen Vorgangsdatensätze der Kasse (dauerhafte Wiederaufnahme, Codex R2-3). */
export async function offeneVorgaenge(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<VorgangsDatensatz[]> {
  const alle = await speicher.transaktion((tx) => tx.alle<VorgangsDatensatz>('vorgang'));
  return alle
    .filter((d) => d.kassenId === kassenId && d.abgeschlossen === undefined)
    .sort((a, b) => a.vorgang.zeitpunkt.localeCompare(b.vorgang.zeitpunkt));
}

/** Erst nach dem Beleg-Zeitlimit (120 s) und etwas Reserve gilt ein Sendeversuch als vorbei. */
export const WIEDERAUFNAHME_AB_MS = 150_000;

/**
 * Codex R2-3: Wiederaufnahme aus den dauerhaften Vorgangsdatensätzen selbst (nicht aus dem
 * Bon-Merker, den ein anderes Fenster leeren kann). Fehlerbericht 25.10.2026: Im Hintergrund wird
 * nur geklärt, was der Server bereits entschieden hat (gebucht, verworfen) – ein Verkauf ohne
 * bestätigte Zahlung wird nie automatisch gebucht, er wartet auf die Bezahlentscheidung an der
 * Kasse. Nachgeholt werden nur Belege einer bestätigten Kartenzahlung bzw. einer an der Kasse
 * festgehaltenen Bezahlbestätigung (`bezahlt`), jeweils mit derselben Vorgangs-ID. Jede Klärung
 * läuft unter der Kassiersperre (Codex-Runde 1, M8): nie zwei Fenster zugleich am selben Vorgang.
 * Liefert die Zahl der noch ungeklärten Vorgänge (gespeichert, nichts gelöscht).
 */
export async function nimmVorgaengeWiederAuf(
  api: VorgangsApi,
  speicher: GeraeteSpeicher,
  kassenId: string,
  jetzt: () => number = Date.now,
  neueNummer?: () => Promise<{ nummer: string; offlineErlaubt: boolean }>,
  nummernkreis?: Nummernkreis,
): Promise<number> {
  await raeumeNachweiseAuf(speicher, jetzt).catch(() => undefined);
  await raeumeAusgaengeAuf(speicher, jetzt).catch(() => undefined);
  let offen = 0;
  for (const d of await offeneVorgaenge(speicher, kassenId)) {
    // Inzwischen (z. B. über einen Ersatz in dieser Runde) abgeschlossen: nicht mehr offen
    const aktuell = await ladeVorgang(speicher, kassenId, d.belegnummer).catch(() => d);
    if (aktuell?.vorgang.id === d.vorgang.id && aktuell.abgeschlossen !== undefined) {
      continue;
    }
    if (jetzt() - Date.parse(d.vorgang.zeitpunkt) < WIEDERAUFNAHME_AB_MS) {
      offen++;
      continue;
    }
    // Im Hintergrund nie aus fachlichen Gründen verwerfen – das entscheidet die Kasse beim
    // „Beleg prüfen“; nur eine vergebene Nummer schließt endgültig ab (Codex R3-1/-2).
    const k = await mitKassiersperre(kassenId, () =>
      klaereOffenenVorgang(api, speicher, kassenId, d.belegnummer, {
        verwerfen: false,
        vorgangId: d.vorgang.id,
        ...(neueNummer === undefined ? {} : { neueNummer }),
        ...(nummernkreis === undefined ? {} : { nummernkreis }),
      }),
    );
    if (k.art === 'unklar' || k.art === 'entscheidung') {
      offen++;
    }
  }
  return offen + ((await ladeReservierung(speicher, kassenId)) === null ? 0 : 1);
}

/**
 * Codex-Runde 1 (K3/K4/K5): dauerhafte Kassierreservierung eines Kassierwegs ohne
 * Vorgangsdatensatz – Kartenzahlungsstart (bis Beleg, Ablehnung oder Klärung) und
 * Lieferdienst-Erfassung (bis zur Antwort). Sie liegt im Gerätespeicher (über alle Fenster
 * derselben Installation) und enthält, was ein anderes Fenster zum Klären braucht. Höchstens eine
 * je Kasse; solange sie besteht, entsteht über keinen anderen Weg ein Vorgang.
 */
export type Kassierreservierung =
  | {
      kassenId: string;
      art: 'karte';
      /** Vorgangs-ID des Kartenzahlungsstarts (P13) */
      id: string;
      /** Start minus Spielraum (ISO), wie `startUnklarSeit` */
      seit: string;
      positionen: unknown[];
      /** Nachgetragen für eine offene Zahlung aus einer früheren Kassenversion (ohne Start-ID) */
      zahlungsreferenz?: string;
      /** Altbestand ohne Start-ID: nur über Zeit und Bestätigung klärbar */
      ohneStartId?: true;
      /**
       * Codex-Stop-Prüfung: Momentaufnahme des Bons beim Start (Kunde, Gutscheine, Empfänger,
       * Notiz, Tisch, bonierter Stand) – ein anderes Fenster braucht sie, um den Beleg der
       * Zahlung mit genau diesem Inhalt zu erstellen (der Server prüft ihn gegen die Freigabe).
       */
      bon?: unknown;
    }
  | {
      kassenId: string;
      art: 'plattform';
      /** Plattform und Bestellnummer */
      id: string;
      seit: string;
      positionen: unknown[];
      /** Unveränderte Nutzdaten: Wiederholen ist am Server idempotent (gleicher Inhalt). */
      erfassung: unknown;
      /** Momentaufnahme des Bons (wie bei der Karte) – nach der Klärung wird genau er abgeschlossen */
      bon?: unknown;
    }
  | {
      kassenId: string;
      art: 'altbon';
      /** Belegnummer eines offenen Bons ohne Vorgangsdatensatz (Kassenversion vor N1) */
      id: string;
      seit: string;
      positionen: unknown[];
    };

function reservierungSchluessel(kassenId: string): string {
  return `kassieren|${kassenId}`;
}

export async function ladeReservierung(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<Kassierreservierung | null> {
  return speicher.transaktion(
    async (tx) =>
      (await tx.get<Kassierreservierung>('meta', reservierungSchluessel(kassenId))) ?? null,
  );
}

/**
 * Reservierung anlegen – nur unter der Kassiersperre nach `pruefeKeinOffenerVorgang`. Liegt schon
 * eine andere vor, `VerkaufUngeklaert`; dieselbe (gleiche Art und ID) bleibt unverändert.
 * Scheitert das Schreiben, wirft es: dann wird nicht kassiert (im Zweifel gesperrt).
 */
export async function reserviereKassieren(
  speicher: GeraeteSpeicher,
  r: Kassierreservierung,
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    const vorhanden = await tx.get<Kassierreservierung>('meta', reservierungSchluessel(r.kassenId));
    if (vorhanden !== undefined) {
      if (vorhanden.art === r.art && vorhanden.id === r.id) {
        return;
      }
      throw new VerkaufUngeklaert(null, vorhanden);
    }
    await tx.put('meta', reservierungSchluessel(r.kassenId), r);
  });
}

/**
 * Codex-Prüfung 4: Ausgang einer Reservierung, dauerhaft und je Reservierung. Ein Bon, der die
 * Reservierung trägt (Bon-Merker `reservierung`, auch in einem neu geladenen, duplizierten oder
 * neu gestarteten Fenster), erfährt so, ob sein Einkauf schon verkauft ist (dann wird er
 * abgeschlossen, nie erneut kassiert) oder nicht (dann ist er wieder frei).
 */
export interface ReservierungsAusgang {
  schluessel: string;
  ergebnis: 'verkauft' | 'nicht_verkauft';
  /** Positionen des reservierten Einkaufs (zum Abschließen des Bons) */
  positionen: unknown[];
  am: string;
  /** Codex-Prüfung 9: benannte Person, wenn ein Mensch den Ausgang festgelegt hat */
  von?: string;
}

function ausgangSchluessel(kassenId: string, art: string, id: string): string {
  return `erledigt|${kassenId}|${art}|${id}`;
}

/**
 * Reservierung freigeben – nur genau diese (Art und ID), nie eine fremde. Mit `ergebnis` wird
 * der Ausgang in derselben Transaktion dauerhaft festgehalten (Codex-Prüfung 4). Ergebnis
 * (Codex-Prüfung 10): der tatsächlich gespeicherte Ausgang – war die Reservierung schon
 * freigegeben (z. B. von einem anderen Fenster), dessen Ausgang, sonst null.
 */
export async function gibReservierungFrei(
  speicher: GeraeteSpeicher,
  kassenId: string,
  art: Kassierreservierung['art'],
  id: string,
  ergebnis?: ReservierungsAusgang['ergebnis'],
  von?: string,
): Promise<ReservierungsAusgang['ergebnis'] | null> {
  return speicher.transaktion(async (tx) => {
    const r = await tx.get<Kassierreservierung>('meta', reservierungSchluessel(kassenId));
    if (r?.art !== art || r.id !== id) {
      const vorhanden = await tx.get<ReservierungsAusgang>(
        'meta',
        ausgangSchluessel(kassenId, art, id),
      );
      return vorhanden?.ergebnis ?? null;
    }
    {
      if (ergebnis !== undefined) {
        const schluessel = ausgangSchluessel(kassenId, art, id);
        await tx.put('meta', schluessel, {
          schluessel,
          ergebnis,
          positionen: r.positionen,
          am: new Date().toISOString(),
          ...(von === undefined ? {} : { von }),
        } satisfies ReservierungsAusgang);
      }
      await tx.delete('meta', reservierungSchluessel(kassenId));
    }
    return ergebnis ?? null;
  });
}

/** Festgehaltener Ausgang einer Reservierung oder null (noch offen bzw. unbekannt). */
export async function ladeReservierungsAusgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
  art: string,
  id: string,
): Promise<ReservierungsAusgang | null> {
  return speicher.transaktion(
    async (tx) =>
      (await tx.get<ReservierungsAusgang>('meta', ausgangSchluessel(kassenId, art, id))) ?? null,
  );
}

/** Ausgänge nach der Aufbewahrungsfrist der Abschlussnachweise entfernen. */
export async function raeumeAusgaengeAuf(
  speicher: GeraeteSpeicher,
  jetzt: () => number = Date.now,
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    for (const w of await tx.alle<Partial<ReservierungsAusgang>>('meta')) {
      if (
        typeof w.schluessel === 'string' &&
        w.schluessel.startsWith('erledigt|') &&
        typeof w.am === 'string' &&
        jetzt() - Date.parse(w.am) > NACHWEIS_AUFBEWAHREN_MS
      ) {
        await tx.delete('meta', w.schluessel);
      }
    }
  });
}

const WARNUNG = 'Bitte diesen Einkauf nicht erneut kassieren.';

/**
 * Fehlerbericht 25.10.2026: Ein Verkauf dieser Kasse ist noch ungeklärt – solange entsteht kein
 * neuer Vorgang (keine Nummer, keine Übertragung), gleich über welchen Kassierweg oder welches
 * Fenster. `offen` = offener Vorgangsdatensatz, sonst `reservierung` (Karte, Lieferdienst,
 * Altbestand).
 */
export class VerkaufUngeklaert extends Error {
  override readonly name = 'VerkaufUngeklaert';

  constructor(
    readonly offen: VorgangsDatensatz | null,
    readonly reservierung: Kassierreservierung | null = null,
  ) {
    super(
      offen !== null
        ? `Verkauf ${offen.belegnummer} noch ungeklärt. ${WARNUNG} Zuerst Belegstatus prüfen.`
        : reservierung?.art === 'karte'
          ? `Kartenzahlung noch ungeklärt. ${WARNUNG} Zuerst „Zahlung prüfen“.`
          : reservierung?.art === 'plattform'
            ? `Lieferdienst-Erfassung ${reservierung.id} noch ungeklärt. ${WARNUNG} Zuerst „Erfassung prüfen“.`
            : `Verkauf ${reservierung?.id ?? ''} noch ungeklärt. ${WARNUNG} Zuerst Belegstatus prüfen.`,
    );
  }
}

/**
 * Zentrale Kassiersperre (Fehlerbericht 25.10.2026, Codex-Runde 1): Liegt für die Kasse ein
 * offener Vorgangsdatensatz (gesendet ohne Ergebnis, nicht gepuffert, nicht abgeschlossen) oder
 * eine Kassierreservierung vor, wirft sie `VerkaufUngeklaert`. `eigene` nimmt genau die
 * Reservierung des eigenen Kassierwegs aus (Beleg nach der eigenen Kartenzahlung). Ein Lesefehler
 * sperrt ebenfalls (im Zweifel kein neuer Vorgang).
 */
export async function pruefeKeinOffenerVorgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
  eigene?: { art: Kassierreservierung['art']; id: string },
): Promise<void> {
  const offen = await offeneVorgaenge(speicher, kassenId);
  const erster = offen[0];
  if (erster !== undefined) {
    throw new VerkaufUngeklaert(erster);
  }
  const r = await ladeReservierung(speicher, kassenId);
  if (r !== null && !(r.art === eigene?.art && r.id === eigene.id)) {
    throw new VerkaufUngeklaert(null, r);
  }
}

/**
 * Codex-Bestätigungsprüfung F5: Tagesabschluss nur ohne ungeklärten Verkauf, ohne Reservierung
 * und ohne gepufferte, noch nicht nachsignierte Offline-Verkäufe dieser Kasse (sie fehlen sonst
 * im Tagesbericht). Unter der Kassiersperre aufrufen.
 */
export async function pruefeAbschlussBereit(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<void> {
  await pruefeKeinOffenerVorgang(speicher, kassenId);
  const gepuffert = (
    await speicher.transaktion((tx) => tx.alle<{ kassenId?: string }>('puffer'))
  ).filter((b) => b.kassenId === kassenId).length;
  if (gepuffert > 0) {
    throw new Error(
      `${String(gepuffert)} Offline-Verkauf/Verkäufe noch nicht nachsigniert – erst synchronisieren, dann den Tagesabschluss protokollieren`,
    );
  }
}

export class KassiersperreNichtVerfuegbar extends Error {
  constructor() {
    super(
      'Dieser Browser bietet keine fensterübergreifende Sperre (Web Locks) – Kassieren ist gesperrt. Bitte einen aktuellen Browser über HTTPS verwenden.',
    );
  }
}

/**
 * Prüfung und Anlage eines neuen Vorgangs sowie jede Klärung laufen je Kasse über alle Fenster
 * nacheinander (Web Locks): Zwei Fenster können so nie gleichzeitig an der Sperre vorbei einen
 * Vorgang anlegen. Codex-Runde 1 (K2): Ohne Web Locks gibt es keine fensterübergreifende Sperre –
 * dann wird nicht kassiert (fail closed), statt nur je Fenster nacheinander.
 */
export function mitKassiersperre<T>(kassenId: string, arbeit: () => Promise<T>): Promise<T> {
  // Manche Umgebungen liefern `null` statt `undefined` (kein Web-Locks-Support)
  const locks = (globalThis.navigator as Navigator | undefined)?.locks as
    LockManager | null | undefined;
  if (locks === undefined || locks === null) {
    return Promise.reject(new KassiersperreNichtVerfuegbar());
  }
  return locks.request(`bilanx.kassieren.${kassenId}`, arbeit);
}

/**
 * Codex-Runde 5: offener Bon aus einer Kassenversion VOR N1 (Merker ohne Vorgangs-ID, kein
 * Datensatz). Ohne Vorgangskennung ordnet die Kasse nie automatisch über die Belegnummer zu:
 * - Puffereintrag gleicher Nummer bzw. Lesefehler → unklar (keine Zuordnung über die Nummer);
 * - Server sperrt die freie Nummer → ein verspäteter Altaufruf belegt sie nie mehr → nicht gebucht;
 * - unter der Nummer liegt ein Beleg → ein Mensch entscheidet (`bestaetige`), ob er zu diesem
 *   Bon gehört; ohne Entscheidung bleibt der Bon unklar.
 */
export async function klaereAltbestandsBon(
  api: Pick<KassenApi, 'belegnummerSperren'>,
  /** Liegt ein Puffereintrag mit dieser Nummer vor? (Lesefehler → Ausnahme) */
  gepuffert: () => Promise<boolean>,
  kassenId: string,
  belegnummer: string,
  bestaetige: (beleg: BelegAntwort) => 'gehoert_dazu' | 'nicht_gebucht' | 'offen',
): Promise<VorgangsKlaerung> {
  // Codex-Runde 6: Ein Puffereintrag gleicher Nummer beweist ohne Vorgangs-ID keine
  // Zugehörigkeit, ein Lesefehler keine Abwesenheit – beides bleibt unklar (nach der
  // Nachsignierung entscheidet ein Mensch über den dann vorhandenen Beleg)
  let imPuffer: boolean;
  try {
    imPuffer = await gepuffert();
  } catch (e: unknown) {
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  if (imPuffer) {
    return {
      art: 'unklar',
      fehler: 'Ein Offline-Eintrag mit dieser Nummer wartet auf die Nachsignierung',
    };
  }
  let s: Awaited<ReturnType<KassenApi['belegnummerSperren']>>;
  try {
    s = await api.belegnummerSperren(
      kassenId,
      belegnummer,
      'Offener Bon einer Kassenversion vor N1 ohne Beleg abgeschlossen',
    );
  } catch (e: unknown) {
    return { art: 'unklar', fehler: e instanceof Error ? e.message : String(e) };
  }
  if (s.stand === 'gesperrt') {
    return { art: 'nicht_gebucht', fehler: 'kein Beleg unter dieser Nummer – Nummer gesperrt' };
  }
  const antwort = bestaetige(s.beleg);
  if (antwort === 'gehoert_dazu') {
    return { art: 'gebucht', beleg: s.beleg };
  }
  if (antwort === 'nicht_gebucht') {
    // Die Nummer gehört einem anderen Beleg: der Altaufruf dieses Bons kann sie nie belegen
    return { art: 'nicht_gebucht', fehler: 'Beleg unter der Nummer gehört nicht zu diesem Bon' };
  }
  return { art: 'unklar', fehler: 'Zuordnung des Belegs offen' };
}
